import axios from 'axios'
import { z, ZodError } from 'zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  BerthFormSchema,
  BerthFormType,
  BerthSchema,
  EditBerthFormSchema,
  EditBerthFormType,
} from '../models/marina'
import { ymdFromDate } from '../utils/ymd'

export const addBerths = async (data: {
  berthsData: BerthFormType
  marinaId: number
  pricing: string
}) => {
  try {
    const parsedBerths = BerthFormSchema.parse(data.berthsData)

    const formattedData = {
      berths: parsedBerths.berths.map((berth) => ({
        marina_id: data.marinaId,
        name: berth,
        min_length: parsedBerths.min_length,
        max_length: parsedBerths.max_length,
        boat_types: parsedBerths.boat_types,
        pricing: data.pricing,
        price:
          data.pricing === 'per_meter'
            ? parsedBerths.price_per_meter
            : parsedBerths.price_flat_fee,
      })),
    }

    const response = await axios.post('/api/berths/', formattedData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return response.data
  } catch (error) {
    if (error instanceof ZodError) {
      throw new Error('Invalid input format. Please check your credentials.')
    } else if (axios.isAxiosError(error)) {
      throw new Error('Login failed, please try again later')
    } else {
      throw new Error('An unexpected error occurred.')
    }
  }
}

export const useAddBerths = () => {
  return useMutation({
    mutationFn: addBerths,
  })
}

const getBerthsByPeriod = async (marina_id: string, period: Date) => {
  try {
    const res = await axios.get(
      `/api/berths/?marina_id=${parseInt(marina_id, 10)}&period=${ymdFromDate(period)}`
    )
    return z.array(BerthSchema).parse(res.data.berths)
  } catch (error) {
    throw new Error('Failed to fetch berths with given period.')
  }
}

export const useBerthsByPeriod = (marina_id: string, period: Date) => {
  return useQuery({
    queryKey: ['berths-by-period', marina_id, period],
    queryFn: () => getBerthsByPeriod(marina_id, period),
    enabled: !!marina_id && !isNaN(parseInt(marina_id, 10)),
  })
}

export const editBerths = async (data: {
  berthsData: EditBerthFormType
  marinaId: number
  pricing: string
}) => {
  try {
    const parsedBerths = EditBerthFormSchema.parse(data.berthsData)

    const formattedData = {
      berths: [
        {
          marina_id: data.marinaId,
          name: parsedBerths.name,
          min_length: parsedBerths.min_length,
          max_length: parsedBerths.max_length,
          boat_types: parsedBerths.boat_types,
          pricing: data.pricing,
          price:
            data.pricing === 'per_meter'
              ? parsedBerths.price_per_meter
              : parsedBerths.price_flat_fee,
        },
      ],
    }

    const response = await axios.post('/api/berths/', formattedData, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return response.data
  } catch (error) {
    if (error instanceof ZodError) {
      throw new Error('Invalid input format. Please check your credentials.')
    } else if (axios.isAxiosError(error)) {
      throw new Error('Login failed, please try again later')
    } else {
      throw new Error('An unexpected error occurred.')
    }
  }
}

export const useEditBerth = () => {
  return useMutation({
    mutationFn: editBerths,
  })
}
